import { flash } from "@xola/ui-kit";
import {
    formatDate,
    getAddOns,
    getDemographicCatalog,
    getExperienceMin,
    isEquipmentBased,
    isScheduled,
    PurchaseItemStatus,
} from "common";
import dayjs from "dayjs";
import { produce } from "immer";
import { isString, sortBy } from "lodash";
import { Except } from "type-fest";
import {
    AllowedPrivacy,
    Arrival,
    Experience,
    GiftOffer,
    GiftOfferType,
    GiftPreference,
    LineItemType,
    Membership,
    Package,
    PaymentLineItemStatus,
    PaymentMethod,
    ProductCatalogItem,
    Purchase,
    PurchaseItemObject,
    Timeslot,
    UnitType,
} from "types";
import { FormTerms, getTerms } from "../components/Product/EditGiftOffer";
import {
    AddonLineItem,
    CreatePurchaseItemLineItem,
    CreatePurchaseItemState,
    CreatePurchaseLineItem,
    CreatePurchaseState,
    DemographicLineItem,
    getExperienceAddonLineItems,
    getExperienceAttendeeLineItems,
    getExperienceDemographicLineItems,
    getExperienceEquipmentLineItems,
    PaymentLineItem,
    useCreatePurchaseStore,
} from "../stores/createPurchaseStore";
import { getDefaultPrice } from "./Experience";
import {
    getExperiencesInPackage,
    getMinMaxForPackage,
    getPackageDemographicCatalog,
    isPartnerPackage,
    isSharedDemographicsPackage,
    isVariableDemographicsPackage,
} from "./Package";
import { getMembershipCatalog } from "@/models/Membership";

export const hasPaymentLineItem = (paymentLineItem: PaymentLineItem, purchase: Purchase | CreatePurchaseState) => {
    const isInvoice = paymentLineItem.payment.method === PaymentMethod.INVOICE;
    const isLater = paymentLineItem.payment.method === PaymentMethod.LATER;
    return !isInvoice && !isLater && !purchase.splitPayment.enabled;
};

export const makeExperiencePurchaseItem = (
    experience: Experience,
    arrival: Arrival | null = null,
    timeslot?: Timeslot,
): CreatePurchaseItemState => {
    const min = getExperienceMin(experience);
    const defaultArrival = arrival ?? { date: formatDate(dayjs().tz()), time: null };
    const privacy =
        (timeslot?.privacy as AllowedPrivacy | undefined) ??
        (experience.allowedPrivacies.length === 1 ? experience.allowedPrivacies[0] : AllowedPrivacy.PUBLIC);
    const isEquipment = isEquipmentBased(experience);
    const demographics = isEquipment ? [] : getDemographicCatalog(experience);

    return {
        quantity: min,
        arrivalDate: defaultArrival.date,
        arrivalTime: defaultArrival.time ?? undefined,
        privacy,
        product: { id: experience.id },
        object: isEquipment
            ? PurchaseItemObject.EQUIPMENT
            : isScheduled(experience)
            ? PurchaseItemObject.EXPERIENCE
            : PurchaseItemObject.ORDER,
        customLineItems: [],
        status: PurchaseItemStatus.DRAFT,
        demographicLineItems: getExperienceDemographicLineItems(demographics, min),
        equipmentLineItems: isEquipment ? getExperienceEquipmentLineItems(experience, timeslot) : undefined,
        attendeeLineItems: isEquipment ? getExperienceAttendeeLineItems(experience) : undefined,
        addonLineItems: getExperienceAddonLineItems(experience),
        lineItems: [],
        seller: { id: experience.seller.id },
        balanceDueOffset: experience.balanceDueOffset ? experience.balanceDueOffset / 1440 : 0,
        ...(timeslot && { timeslot: timeslot }),
    };
};

interface makeGiftOfferItemProps {
    giftOffer: GiftOffer;
    giftOfferTerms: FormTerms;
    giftPreference: GiftPreference;
    experience?: Experience;
    giftCode?: string;
    giftValue?: number;
}

export const makeGiftOfferItem = ({
    giftOffer,
    giftOfferTerms,
    giftPreference,
    experience,
    giftCode,
    giftValue,
}: makeGiftOfferItemProps): CreatePurchaseItemState => {
    const giftPurchaseRule = giftOffer.purchaseRule;

    const giftTerms = getTerms(giftOffer, giftPurchaseRule);
    const isVariable = giftOfferTerms.type === GiftOfferType.VARIABLE || giftTerms.type === GiftOfferType.VARIABLE;

    const calculatedGiftValue =
        isVariable && giftTerms.minimumPrice && giftTerms.minimumPrice > 0 ? giftTerms.minimumPrice : 1;

    const baseLineItem: any = [
        {
            code: "gift",
            amount: giftValue ?? calculatedGiftValue,
            type: LineItemType.GIFT,
        },
    ];

    const lineItems = giftOffer.system
        ? makeSystemGiftLineItems(giftOffer, giftPreference.showDemographics, experience, giftValue)
        : baseLineItem;

    const demographicLineItems = giftOffer.system && !lineItems ? makeGiftDemographicLineItems(experience!) : [];
    const isEquipment = experience ? isEquipmentBased(experience) : false;

    const giftOfferItem: Except<
        CreatePurchaseItemState,
        "arrivalDate" | "privacy" | "customLineItems" | "addonLineItems" | "wareLineItems"
    > = {
        object: PurchaseItemObject.GIFT,
        recipientName: "",
        recipientEmail: "",
        message: "",
        sendToRecipient: false,
        product: { id: giftOffer.id },
        source: "office",
        lineItems: lineItems ?? [],
        demographicLineItems: demographicLineItems,
        equipmentLineItems: isEquipment ? getExperienceEquipmentLineItems(experience!) : undefined,
        status: PurchaseItemStatus.DRAFT,
        ...(giftCode && { code: giftCode, isScannedGiftCard: true }),
        quantity: experience ? getExperienceMin(experience) : 1,
    };

    return giftOfferItem as CreatePurchaseItemState;
};

interface MakeMembershipItemProps {
    membership: Membership;
}

export const makeMembershipItem = ({
    membership,
}: MakeMembershipItemProps): Omit<
    CreatePurchaseItemState,
    "privacy" | "arrivalDate" | "customLineItems" | "demographicLineItems" | "addonLineItems" | "wareLineItems"
> => {
    return {
        object: PurchaseItemObject.MEMBERSHIP,
        product: { id: membership.id },
        source: "office",
        status: PurchaseItemStatus.DRAFT,
        lineItems: getMembershipPurchaseLineItems(membership) as CreatePurchaseItemLineItem[],
        quantity: 1,
    };
};

const makeSystemGiftLineItems = (
    giftOffer: GiftOffer,
    showDemographics: boolean,
    experience?: Experience,
    giftValue?: number,
) => {
    if (giftOffer.baseGiftProduct) {
        return [{ code: "gift", amount: giftValue ?? 1, type: LineItemType.GIFT }];
    }

    if (!experience) {
        // Should never happen, it's here mostly for the type checking
        const error = "The gift offer has no experience";
        flash.show({ text: error, color: "danger" });
        throw new Error(error);
    }

    if (!showDemographics) {
        return [{ code: "gift", amount: giftValue ?? getDefaultPrice(experience), type: LineItemType.GIFT }];
    }
};

const makeGiftDemographicLineItems = (experience: Experience) => {
    return getDemographicCatalog(experience).map((demographic, index) => ({
        type: LineItemType.DEMOGRAPHIC,
        quantity: index === 0 ? getExperienceMin(experience) : 0,
        template: { id: demographic.template!.id },
    })) as DemographicLineItem[];
};

export const hasCreditCardLineItem = (purchase: CreatePurchaseState) => {
    return purchase.lineItems.find((lineItem) => lineItem.payment?.method === PaymentMethod.CREDIT_CARD);
};

export const makePackagePurchaseItem = (
    package_: Package,
    experiences: Experience[],
    arrival: Arrival | null = null,
    timeslots?: Timeslot[],
) => {
    const packageInitialData: any = [];
    const packageExperiences = getExperiencesInPackage(package_, experiences);
    const [min] = getMinMaxForPackage(package_, experiences);

    for (const experience of packageExperiences) {
        const experienceArrival = timeslots
            ? timeslots?.find((timeslot) => timeslot.product === experience.id)
            : arrival;

        const demographics = isPartnerPackage(package_)
            ? getDemographicCatalog(experience)
            : getPackageDemographicCatalog(package_, experience.id);

        if (isVariableDemographicsPackage(package_) && !experienceArrival) {
            const error = "There is no active schedule for some of the products in this package";
            flash.danger(error);
        }

        const defaultArrival = experienceArrival ?? { date: formatDate(dayjs()), time: null };
        const minQuantity = isSharedDemographicsPackage(package_) ? min : getExperienceMin(experience);
        const privacy =
            experience.allowedPrivacies.length === 1 ? experience.allowedPrivacies[0] : AllowedPrivacy.PUBLIC;

        packageInitialData.push({
            quantity: minQuantity,
            arrivalDate: defaultArrival.date,
            arrivalTime: defaultArrival.time ?? undefined,
            privacy,
            product: { id: experience.id },
            object: PurchaseItemObject.EXPERIENCE,
            parentProduct: { id: package_.id, object: package_.object },
            seller: { id: experience.seller.id },
            lineItems: [],
            demographicLineItems: getExperienceDemographicLineItems(demographics, minQuantity),
            addonLineItems: getExperiencePurchaseAddonLineItems(experience),
        });
    }

    return packageInitialData;
};

export const isMistPurchase = (purchase: CreatePurchaseState | Purchase) => {
    return purchase.items.length > 1;
};

export const getDefaultChoiceTemplateId = (choiceAddon: ProductCatalogItem) => {
    const defaultChoice = choiceAddon?.defaultChoice;
    return choiceAddon?.items?.find((item) => item.id === defaultChoice)?.template?.id ?? "";
};

export const getCreatePurchaseAddonLineItems = (lineItems: AddonLineItem[]) => {
    return lineItems.filter((lineItem) =>
        "type" in lineItem && isString(lineItem.type)
            ? [LineItemType.ADDON, LineItemType.PICKUP].includes(lineItem.type)
            : false,
    );
};

const getExperiencePurchaseAddonLineItems = (experience: Experience) => {
    const experienceAddons = experience ? getAddOns(experience) : [];
    const choicesAddonsWithDefaultChoice = experienceAddons.filter(
        (addon) => addon.items && addon?.defaultChoice && !!getDefaultChoiceTemplateId(addon),
    );

    const addonLineItems = sortBy(choicesAddonsWithDefaultChoice, "sequence").map((choiceAddon) => ({
        type: choiceAddon.unitType === UnitType.PICKUP ? LineItemType.PICKUP : LineItemType.ADDON,
        quantity: 1,
        template: { id: getDefaultChoiceTemplateId(choiceAddon) },
        catalog: { template: getDefaultChoiceTemplateId(choiceAddon) },
    }));

    return addonLineItems;
};

export const isReservePurchaseProcess = (
    paymentMethod: PaymentMethod | ID,
    dueNow: number,
    paymentStatus?: PaymentLineItemStatus,
) => {
    return paymentMethod === PaymentMethod.LATER || paymentStatus === PaymentLineItemStatus.PENDING || dueNow === 0;
};

const getMembershipPurchaseLineItems = (membership: Membership) => {
    const catalogItems = getMembershipCatalog(membership);

    return [
        ...sortBy(catalogItems, "sequence").map((catalogItem) => ({
            type: LineItemType.MEMBERSHIP,
            quantity: 1,
            template: { id: catalogItem.template!.id },
        })),
    ];
};

export const resetPaymentLineItem = () => {
    useCreatePurchaseStore.setState(
        produce((draft) => {
            const payment = draft.paymentLineItem.payment;

            if (payment?.method !== PaymentMethod.CREDIT_CARD) {
                return;
            }
            if (!payment.card) {
                return;
            }
            // This is the case when we need to remove the card from the payment line item,
            // when the seller removes the selected organizer by clicking on "Remove Customer" button
            // If not removed, the card will be sent to the backend and will cause an error
            delete payment.card;
        }),
    );
};

export const getCashPaymentLineItem = (lineItems: CreatePurchaseLineItem[]) => {
    return lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.CASH;
    });
};
